export const roles = [
  "OWNER",
  "ADMIN",
  "SCHEDULER",
  "FRONT_DESK",
  "STAFF",
  "CRM_AGENT",
  "GUEST",
] as const;

export type Role = (typeof roles)[number];

export const portalCapabilityActions = [
  "view",
  "create",
  "write",
  "publish",
  "manage",
] as const;

export type PortalCapabilityAction = (typeof portalCapabilityActions)[number];

/**
 * Stable portal areas. Values match protected navigation IDs so an area's
 * `view` grant is the single source of truth for menu and route visibility.
 */
export const portalCapabilityAreas = [
  "guest-overview",
  "guest-profile",
  "guest-appointments",
  "guest-forms",
  "guest-messages",
  "guest-documents",
  "guest-invoices",
  "staff-overview",
  "staff-today",
  "staff-schedule",
  "staff-check-in",
  "staff-checkout",
  "staff-print",
  "staff-crm",
  "staff-conversations",
  "admin-crm",
  "admin-homepage",
  "admin-conversations",
  "admin-calendar",
  "admin-scheduler",
  "admin-appointments",
  "admin-resources",
  "admin-guests",
  "admin-documents",
  "admin-invoices",
  "admin-roles",
  "admin-audit",
  "admin-settings",
  "admin-site-setup",
] as const;

export type PortalCapabilityArea = (typeof portalCapabilityAreas)[number];
export type PortalCapabilityPermission =
  `${PortalCapabilityArea}.${PortalCapabilityAction}`;

export interface PortalCapabilityActionDescriptor {
  readonly key: PortalCapabilityAction;
  readonly label: string;
}

export interface PortalCapabilityAreaDescriptor {
  readonly key: PortalCapabilityArea;
  readonly label: string;
  readonly section: "Guest" | "Staff" | "Administration";
  readonly actions: readonly PortalCapabilityActionDescriptor[];
}

export const portalCapabilityActionCatalog: readonly PortalCapabilityActionDescriptor[] = [
  { key: "view", label: "View / list" },
  { key: "create", label: "Create" },
  { key: "write", label: "Edit / save" },
  { key: "publish", label: "Publish / approve" },
  { key: "manage", label: "Administer / delete" },
];

const portalCapabilityAreaLabels: Readonly<
  Record<PortalCapabilityArea, string>
> = {
  "guest-overview": "Overview",
  "guest-profile": "Profile",
  "guest-appointments": "Appointments",
  "guest-forms": "Forms",
  "guest-messages": "Messages",
  "guest-documents": "Documents",
  "guest-invoices": "Invoices",
  "staff-overview": "Overview",
  "staff-today": "Today",
  "staff-schedule": "Schedule",
  "staff-check-in": "Check in",
  "staff-checkout": "Checkout",
  "staff-print": "Print",
  "staff-crm": "CRM",
  "staff-conversations": "Conversations",
  "admin-crm": "CRM",
  "admin-homepage": "Homepage",
  "admin-conversations": "Conversations",
  "admin-calendar": "Calendar",
  "admin-scheduler": "Scheduler",
  "admin-appointments": "Appointments",
  "admin-resources": "Resources",
  "admin-guests": "Guests",
  "admin-documents": "Documents",
  "admin-invoices": "Billing",
  "admin-roles": "Roles and access",
  "admin-audit": "Audit",
  "admin-settings": "Settings",
  "admin-site-setup": "Site setup",
};

function portalCapabilitySection(
  area: PortalCapabilityArea,
): PortalCapabilityAreaDescriptor["section"] {
  if (area.startsWith("guest-")) return "Guest";
  if (area.startsWith("staff-")) return "Staff";
  return "Administration";
}

export const portalCapabilityAreaCatalog: readonly PortalCapabilityAreaDescriptor[] =
  portalCapabilityAreas.map((area) => ({
    key: area,
    label: portalCapabilityAreaLabels[area],
    section: portalCapabilitySection(area),
    actions: portalCapabilityActionCatalog,
  }));

export const legacyPermissions = [
  "content.manage",
  "settings.credentials.manage",
  "settings.connectors.metadata.manage",
  "settings.connectors.rotation.request",
  "roles.manage",
  "schedule.read",
  "schedule.write",
  "schedule.override",
  "guest.contact.read",
  "guest.own.read",
  "guest.own.write",
  "visit.assigned.read",
  "visit.operations.write",
  "documents.authorized.read",
  "documents.manage",
  "billing.status.read",
  "billing.manage",
  "crm.manage",
  "audit.read",
] as const;

export type LegacyPermission = (typeof legacyPermissions)[number];
export type Permission = LegacyPermission | PortalCapabilityPermission;

export interface PortalRoleAccessRole {
  readonly key: Role;
  readonly label: string;
  readonly isActive: boolean;
  readonly permissions: readonly PortalCapabilityPermission[];
  readonly revision: number;
}

export interface PortalRoleAccessSnapshot {
  readonly schemaVersion: 1;
  readonly organizationId: string;
  readonly actorRole: Role;
  readonly roles: readonly PortalRoleAccessRole[];
  readonly areas: readonly PortalCapabilityAreaDescriptor[];
}

export interface ReplacePortalRoleAccessRequest {
  readonly permissions: readonly PortalCapabilityPermission[];
  readonly expectedRevision: number;
}

export interface ReplacePortalRoleAccessResponse {
  readonly role: PortalRoleAccessRole;
  readonly actorSessionRefreshRequired: boolean;
}

export function portalCapabilityPermission(
  area: PortalCapabilityArea,
  action: PortalCapabilityAction,
): PortalCapabilityPermission {
  return `${area}.${action}`;
}

const generatedPortalPermissions = portalCapabilityAreas.flatMap((area) =>
  portalCapabilityActions.map((action) =>
    portalCapabilityPermission(area, action),
  ),
);

export const permissions: readonly Permission[] = [
  ...legacyPermissions,
  ...generatedPortalPermissions,
];

const knownRoles = new Set<string>(roles);
const knownLegacyPermissions = new Set<string>(legacyPermissions);
const knownPortalCapabilityAreas = new Set<string>(portalCapabilityAreas);
const knownPortalCapabilityActions = new Set<string>(portalCapabilityActions);

export function isRole(value: string): value is Role {
  return knownRoles.has(value);
}

export function isLegacyPermission(value: string): value is LegacyPermission {
  return knownLegacyPermissions.has(value);
}

export function isPortalCapabilityArea(
  value: string,
): value is PortalCapabilityArea {
  return knownPortalCapabilityAreas.has(value);
}

export function isPortalCapabilityAction(
  value: string,
): value is PortalCapabilityAction {
  return knownPortalCapabilityActions.has(value);
}

export function isPortalCapabilityPermission(
  value: string,
): value is PortalCapabilityPermission {
  const separator = value.lastIndexOf(".");
  if (separator <= 0 || separator === value.length - 1) return false;
  return (
    isPortalCapabilityArea(value.slice(0, separator)) &&
    isPortalCapabilityAction(value.slice(separator + 1))
  );
}

export interface AuthorizationSubject {
  readonly userId: string;
  readonly roles: readonly Role[];
  readonly permissions: readonly Permission[];
  readonly locationIds: readonly string[];
}

export function can(
  subject: AuthorizationSubject,
  permission: Permission,
  locationId?: string,
) {
  if (!subject.permissions.includes(permission)) return false;
  return !locationId || subject.locationIds.includes(locationId);
}

export function canAccessPortalArea(
  subject: AuthorizationSubject,
  area: PortalCapabilityArea,
  action: PortalCapabilityAction = "view",
  locationId?: string,
) {
  if (locationId && !subject.locationIds.includes(locationId)) return false;
  return hasPortalCapabilityPermission(subject.permissions, area, action);
}

export function hasPortalCapabilityPermission(
  grantedPermissions: readonly string[],
  area: PortalCapabilityArea,
  action: PortalCapabilityAction = "view",
): boolean {
  const required = portalCapabilityPermission(area, action);
  return (
    grantedPermissions.includes(required) ||
    legacyPermissionsForPortalCapability(area, action).some((permission) =>
      grantedPermissions.includes(permission),
    )
  );
}

export function canManagePublicContent(subject: AuthorizationSubject) {
  return canAccessPortalArea(subject, "admin-homepage", "manage");
}

/**
 * Narrow compatibility bridge for grants approved before granular portal
 * capabilities existed. It keeps current users operational during a no-seed
 * rollout without inferring authority from role names or granting role access.
 * Direct capability grants always remain the preferred source of truth.
 */
export function legacyPermissionsForPortalCapability(
  area: PortalCapabilityArea,
  action: PortalCapabilityAction,
): readonly LegacyPermission[] {
  const permissions: LegacyPermission[] = [];
  const add = (...values: readonly LegacyPermission[]) => {
    permissions.push(...values);
  };

  if (area === "admin-homepage") add("content.manage");
  if (area === "admin-roles") add("roles.manage");
  if (area === "admin-audit" && action === "view") add("audit.read");

  if (area === "admin-settings") {
    if (action === "view") {
      add(
        "settings.credentials.manage",
        "settings.connectors.metadata.manage",
        "settings.connectors.rotation.request",
      );
    }
    if (action === "write") add("settings.connectors.metadata.manage");
    if (action === "manage") {
      add("settings.credentials.manage", "settings.connectors.rotation.request");
    }
  }

  if (
    area === "admin-crm" ||
    area === "admin-conversations" ||
    area === "staff-crm" ||
    area === "staff-conversations"
  ) {
    add("crm.manage");
  }

  if (
    area === "staff-schedule" ||
    area === "staff-today" ||
    area === "admin-calendar" ||
    area === "admin-scheduler" ||
    area === "admin-appointments"
  ) {
    if (action === "view") add("schedule.read", "schedule.write");
    if (action === "create" || action === "write") add("schedule.write");
    if (action === "manage") add("schedule.override");
  }

  if (
    area === "staff-overview" ||
    area === "staff-check-in" ||
    area === "staff-checkout" ||
    area === "staff-print"
  ) {
    if (action === "view") add("visit.assigned.read");
    if (action === "create" || action === "write") {
      add("visit.operations.write");
    }
  }

  if (area === "admin-guests" && action === "view") {
    add("guest.contact.read");
  }
  if (area === "admin-documents") {
    if (action === "view") add("documents.authorized.read", "documents.manage");
    if (action !== "view") add("documents.manage");
  }
  if (area === "admin-invoices") {
    if (action === "view") add("billing.status.read", "billing.manage");
    if (action !== "view") add("billing.manage");
  }
  if (area === "guest-documents" && action === "view") {
    add("documents.authorized.read");
  }
  if (area === "guest-invoices" && action === "view") {
    add("billing.status.read");
  }
  if (area.startsWith("guest-") && action === "view") add("guest.own.read");
  if (area.startsWith("guest-") && (action === "create" || action === "write")) {
    add("guest.own.write");
  }

  return [...new Set(permissions)];
}
